import { JSONValue, SourceUIPart, UIMessage } from '@ai-sdk/ui-utils';

/**
 * 各メッセージの型
 *
 * バックエンド定義ではroleとcontentのみだが、partsも含まれる
 * */
export type RagChatMessageItem = UIMessage;

/**
 * AIアシスタントのリクエストパラメータ
 *
 * Vercel AI SDK の useChatフックのreturnのhandleSubmitを利用してリクエストを送信する。
 * */
export type RagChatApiRequestParams = {
  /**
   * セッションID
   *
   * 自動的に生成されリクエストに付与されるが、useChatの引数に任意idを指定することも可能。
   */
  id: string;
  /**
   * 会話履歴
   *
   * リクエストに自動的に付与される。
   */
  messages: RagChatMessageItem[];
  /**
   * 最新の質問
   *
   * handleSubmitの引数に渡すとリクエストに付与される。messagesにも同様の内容が含まれるが、バックエンドの回答生成にはこちらの情報を利用する想定。
   */
  question: string;
};

/**
 * AIアシスタントのレスポンスの型
 *
 * AIの回答はストリーミングレスポンスで受け取るが、OpenAPI 3.0では表現できない。
 * フロントエンドとしてはVercel AI SDK の useChatフックのreturnのmessagesを利用して回答を取得する。
 * https://sdk.vercel.ai/docs/reference/ai-sdk-ui/use-chat#messages
 * なお、claris-backendのapi/docs/openapi.yamlにはフロントエンドとの合意のため以下のように定義してある。
 */
export type RagChatResponse = {
  /**
   * 会話履歴（リファレンス、chatHistoryIdの情報などが含まれる）
   */
  messages?: RagChatMessageItem[];
} & RagChatCustomData;

export type Source = SourceUIPart;

/** リファレンス情報の型 */
export type RagChatReferenceItem = {
  title: string;
  url: string;
  marker: number;
};

/**
 * メッセージのannotationの型がchatHistoryIdを持つオブジェクトであることを確認するための型ガード
 */
export const isChatHistoryId = (
  v: Extract<RagChatMessageItem['annotations'], JSONValue[]>[number]
): v is {
  chatHistoryId: string;
} => {
  return (
    typeof v === 'object' &&
    v !== null &&
    'chatHistoryId' in v &&
    typeof (v as { chatHistoryId: string }).chatHistoryId === 'string'
  );
};

/**
 * AIアシスタントのレスポンスのカスタムデータの型
 *
 * Vercel AI SDK の useChatフックのreturnのdataがこの型を持つ。
 * 基本的にはmessagesの内容を利用するが、カスタムデータを利用する場合はchatHistoryIdでmessagesを特定する必要がある。
 *
 */
export type RagChatCustomData = {
  chatHistoryId: string;
  sessionId: string;
  reference_documents: RagChatReferenceItem[];
  timestamp: string;
  prompt: string;
};

/**
 * AIアシスタントのレスポンスのカスタムデータの型ガード
 *
 */
export const isRagChatCustomData = (
  item: unknown
): item is RagChatCustomData => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'chatHistoryId' in item &&
    'sessionId' in item &&
    'reference_documents' in item &&
    'timestamp' in item &&
    'prompt' in item
  );
};
